import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface FieldLabelProps {
  label: ReactNode;
  /** Appends the "*" required marker from the Figma field labels. */
  required?: boolean;
  /** Plain-weight text rendered after the label, e.g. "512x512px". */
  detail?: ReactNode;
  explanation?: ReactNode;
  className?: string;
}

/** Shared label block: bold 14px label (+ optional detail) and muted explanation. */
export function FieldLabel({
  label,
  required,
  detail,
  explanation,
  className,
}: FieldLabelProps) {
  return (
    <div className={cn('flex flex-col text-sm leading-5', className)}>
      <div className="font-bold text-white">
        {label}
        {required ? '*' : null}
        {detail ? (
          <>
            {': '}
            <span className="font-normal">{detail}</span>
          </>
        ) : null}
      </div>
      {explanation ? (
        <div className="font-normal text-seeker-400">{explanation}</div>
      ) : null}
    </div>
  );
}

/** Inline validation copy: orange 12px with a leading warning glyph. */
export function FieldFeedback({
  children,
  className,
  tone = 'warning',
}: {
  children: ReactNode;
  className?: string;
  tone?: 'warning' | 'success' | 'muted';
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-1 text-xs leading-4 [&>svg]:size-4 [&>svg]:shrink-0',
        tone === 'warning' && 'text-orange-400',
        tone === 'success' && 'text-green-400',
        tone === 'muted' && 'text-seeker-400',
        className,
      )}
    >
      {children}
    </div>
  );
}
